import { Injectable } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { Location } from '@angular/common';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { Analytics, getAnalytics, logEvent, EventParams, setConsent, ConsentStatusString } from 'firebase/analytics';

import { environment } from '@src/environments/environment';
import { fbConfig } from '@src/config/firebase.config';

const app = initializeApp(fbConfig);

@Injectable({
	providedIn: 'root'
})

export class AnalyticsService {
	analyticsEnabled: boolean = environment.analyticsEnabled;
	cookies: string[] = document.cookie.split(';') || [];

	cbState: boolean = true;

	metaInfo = {
		'home': {
			'title': null,
			'description': null
		},
		'privacyPolicy': {
			'title': 'Nick Hudson Dev & Design | Privacy Policy',
			'description': 'Learn how this website collects, uses, stores, and protects your information, including analytics, contact form submissions, newsletter subscriptions, and your privacy rights.'
		},
		'termsOfUse': {
			'title': 'Nick Hudson Dev & Design | Terms of Use',
			'description': 'Review the terms governing your use of this website, including intellectual property, acceptable use, disclaimers, and limitations of liability.'
		},
		'cookiePolicy': {
			'title': 'Nick Hudson Dev & Design | Cookie Policy',
			'description': 'Learn how this website uses cookies and similar technologies to improve performance, analyze traffic, remember preferences, and enhance your browsing experience.'
		},
		'four04': {
			'title': 'Nick Hudson Dev & Design | 404 Page Not Found',
			'description': 'Sorry, page or content not found!'
		},
		'blog': {
			'title': 'Nick Hudson Dev & Design | Blog Home',
			'description': 'A journal covering real lessons learned from work and life, with insights on tech, design, digital marketing, and modern workflows. Oh, and there will be fun stuff too. 😊 — | Brooklyn, New York'
		},
		'blogEntries': {
			'title': 'Nick Hudson Dev & Design | Blog Entry',
			'description': ''
		},
		'blogCategories': {
			'title': 'Nick Hudson Dev & Design | Blog Category Listing',
			'description': ''
		},
		'blogTags': {
			'title': 'Nick Hudson Dev & Design | Blog Tag Listing',
			'description': ''
		}
	}

	constructor(
		public location: Location,
		public title: Title,
		public meta: Meta
	) { } 

	analytics(): Analytics {
		if (this.cookieName('consent'))
			this.cbState = false;

		return getAnalytics(app);
	}

	async setCookie(prefix: string): Promise<void> {
		const gtag = window['gtag'] || null;

		if (gtag) {
			await gtag(
				'config', 
				fbConfig.measurementId, 
				{
					cookie_prefix: prefix,
					cookie_domain: environment.cookieDomain,
					cookie_expires: 60 * 60 * 24 * 28,
					cookie_update: false
				}
			);
		}
	}

	cookieName = (name: string): boolean => {
		return this.cookies.some((item) =>
			item.trim().startsWith(name)
		)
	};

	updateConsent(status: ConsentStatusString, statusType?: string): void {
		const settings = {
			ad_storage: status,
			analytics_storage: status,
			ad_user_data: status,
			ad_personalization: status
		},

		gtag = window['gtag'] || null;
		
		if (statusType === 'update' && gtag) {
			gtag('consent', settings);
			return;
		}

		setConsent(settings);
	} 

	logEvent(event: string, eventParams: EventParams): void {
		if (!this.analyticsEnabled) 
			return;

		return logEvent(
			this.analytics(), 
			event, 
			eventParams
		);
	}

	pageView(): void {
		this.logEvent('page_view', {
			page_title: this.title.getTitle(),
			page_location: window.location.href,
			page_path: this.location.path(false)
		})
	}

	trackInput($event: any): void {
		const targ = $event.target;

		this.logEvent(
			'user_input',
			{
				input_type: $event.type,
				js_name: $event.constructor.name,
				source: targ.src || targ.cite || targ.data || targ.poster || null,
				href: targ.href || targ.parentElement.href || targ.formaction || targ.action || null,
				value:
					targ.checked ||
					targ.selected ||
					targ.value ||
					targ.alt ||
					targ.textContent.slice(0, 254) ||
					targ.outerHTML.slice(0, 254) ||
					null,
				description:
					targ.dataset.content ||
					targ.parentElement.dataset.content ||
					targ.alt ||
					targ.name ||
					targ.placeholder ||
					targ.innerText ||
					'input'
			}
		)
	}

	setHtmlMeta(title?: string, desc?: string): void {
		const location = this.location.path(false);

		if (!this.metaInfo.home.title || !this.metaInfo.home.description) {
			this.metaInfo.home.title = this.title.getTitle();
			this.metaInfo.home.description = this.meta.getTag('name="description"').content
		}

		if (location.endsWith('/privacy')) {
			this.title.setTitle(title || this.metaInfo.privacyPolicy.title)
			this.meta.updateTag({ name: 'description', content: desc || this.metaInfo.privacyPolicy.description })
		} else if (location.endsWith('/terms-of-use')) {
			this.title.setTitle(title || this.metaInfo.termsOfUse.title)
			this.meta.updateTag({ name: 'description', content: desc || this.metaInfo.termsOfUse.description })
		} else if (location.endsWith('/cookies')) {
			this.title.setTitle(title || this.metaInfo.cookiePolicy.title)
			this.meta.updateTag({ name: 'description', content: desc || this.metaInfo.cookiePolicy.description })
		} else if (location.endsWith('/blog')) {
			this.title.setTitle(title || this.metaInfo.blog.title)
			this.meta.updateTag({ name: 'description', content: desc || this.metaInfo.blog.description })
		} else if (location.includes('entries')) {
			this.title.setTitle(title || this.metaInfo.blogEntries.title)
			this.meta.updateTag({ name: 'description', content: desc || this.metaInfo.blog.description })
		} else if (location.includes('categories')) {
			this.title.setTitle(title || this.metaInfo.blogCategories.title)
			this.meta.updateTag({ name: 'description', content: desc || this.metaInfo.blog.description })
		} else if (location.includes('tags')) {
			this.title.setTitle(title || this.metaInfo.blogTags.title)
			this.meta.updateTag({ name: 'description', content: desc || this.metaInfo.blog.description })
		} else if (title && desc) {
			this.title.setTitle(title)
			this.meta.updateTag({ name: 'description', content: desc })
		}
	}
}